import { ProfileModuleType } from '@up2stay/up2promo-api-client';
import { Button } from '@up2stay/up2promo-ui';
import { Label } from '@up2stay/up2promo-ui';
import { Skeleton } from '@up2stay/up2promo-ui';
import { TeamMembersModuleDisplay } from '@up2stay/up2promo-ui/profile/modules/team-members-module-display';
import { ExternalLink, Users } from 'lucide-react';

import type { ModuleEntryGovernanceProps } from '@/core/components/profile/module-entry-governance';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useTeamMembersDisplay } from '@/core/hooks/use-team-members-display';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useAuthStore } from '@/core/store/auth.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import type { TeamMembersModuleConfig } from '@/core/types/profile-modules';

interface TeamMembersModuleConfigProps extends ModuleEntryGovernanceProps {
  config: TeamMembersModuleConfig;
  onChange: (config: TeamMembersModuleConfig) => void;
  fieldLockedHintMessage?: string;
  /** When editing a template, preview members from assigned team(s). */
  templateTeamIds?: string[];
  /** Workspace owners/admins only (links to Teams admin UI). */
  showManageTeamLink?: boolean;
}

export function TeamMembersModuleConfigComponent({
  config: _config,
  onChange: _onChange,
  templateTeamIds,
  showManageTeamLink = false,
}: TeamMembersModuleConfigProps) {
  const dict = useDictionary();
  const tm = dict.profiles.edit.modules.teamMembersModule;
  const currentWorkspace = useCurrentWorkspace();
  const { navigateShell } = useNavigateShell();
  const selfUserId = useAuthStore((state) => state.user?.id ?? null);
  const selfUsername = useAuthStore((state) => state.user?.username ?? null);
  const workspaceId = currentWorkspace?.id;

  const { teamId, teamName, members, isLoading } = useTeamMembersDisplay(
    workspaceId,
    { templateTeamIds, workspaceUsername: currentWorkspace?.username ?? null },
  );

  // A template has no "own" profile: show every member of the assigned team.
  // Hiding the viewer made a one-person team look empty (DEV-821).
  const isTemplate = templateTeamIds !== undefined;
  const visibleMembers = members.filter((m) => {
    if (isTemplate) return true;
    const memberUserId = (m.userId ?? m.id ?? null) as string | null;
    if (selfUserId && memberUserId && memberUserId === selfUserId) return false;
    if (selfUsername && m.username && m.username === selfUsername) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <Label>{tm.title}</Label>
        <p className="text-sm text-muted-foreground">
          {isTemplate ? tm.descriptionTemplate : tm.description}
        </p>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-14 w-full rounded-lg" />
            <Skeleton className="h-14 w-full rounded-lg" />
          </div>
        ) : !teamId ? (
          <p className="py-2 text-sm text-muted-foreground">
            {isTemplate ? tm.noTeamTemplate : tm.noTeam}
          </p>
        ) : visibleMembers.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">{tm.emptyTeam}</p>
        ) : (
          <div className="space-y-2">
            {teamName ? (
              <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Users className="h-3.5 w-3.5" aria-hidden />
                {tm.teamLabel.replace('{name}', teamName)}
              </p>
            ) : null}
            <TeamMembersModuleDisplay
              module={{
                type: ProfileModuleType.TEAM_MEMBERS,
                enabled: true,
                order: 0,
                config: { members: visibleMembers, layout: 'grid', columns: 3 },
              }}
              workspaceUsername={currentWorkspace?.username ?? null}
              currentUsername={selfUsername ?? undefined}
              profileBaseUrl=""
              getFileUrl={(p) => p}
            />
          </div>
        )}
        {showManageTeamLink && teamId && workspaceId ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() =>
              navigateShell(shellPaths.morePeopleTeamDetail(teamId))
            }
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {tm.manageInTeam}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
